import type { DateRange } from "../hooks/useGlobalDateRange";

// =============================================================================
// Date Range Navigation - Prev/Next Date Range Utility
// =============================================================================
// PURPOSE: Navigates date ranges forward/backward by their own span (dynamic jumps).
//
// ARCHITECTURE:
// - Month-aligned ranges (start on 1st, end on last day or today) navigate by calendar month
// - All other ranges use dynamic span: 7-day range jumps 7 days, 30-day jumps 30 days
// - Normalizes to midnight (00:00:00) to avoid time drift
//
// KEY DECISIONS:
// - Month detection is heuristic: start.getDate() === 1 AND (end is last of month OR today)
// - Used by dashboard, leaderboard, and analytics pages for consistent navigation
// =============================================================================

/**
 * Detects whether a date range covers an entire calendar year, or year-to-date.
 * True when: start is Jan 1 of year X AND (end is Dec 31 of year X OR end is today and today.year === X).
 */
function isCalendarYear(start: Date, end: Date): boolean {
  if (start.getMonth() !== 0 || start.getDate() !== 1) return false;
  const startYear = start.getFullYear();

  // Full year: end is Dec 31 of same year
  if (end.getMonth() === 11 && end.getDate() === 31 && end.getFullYear() === startYear) {
    return true;
  }

  // Year-to-date: end is today AND today's year === start's year
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endNorm = new Date(end);
  endNorm.setHours(0, 0, 0, 0);
  if (endNorm.getTime() === today.getTime() && today.getFullYear() === startYear) {
    return true;
  }

  return false;
}

/**
 * Detects whether a date range is exactly a calendar quarter
 * (Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec) of a single year.
 */
function isCalendarQuarter(start: Date, end: Date): boolean {
  if (start.getDate() !== 1) return false;
  const startMonth = start.getMonth();
  // Quarter starts: Jan(0), Apr(3), Jul(6), Oct(9)
  if (startMonth !== 0 && startMonth !== 3 && startMonth !== 6 && startMonth !== 9) return false;
  if (end.getFullYear() !== start.getFullYear()) return false;

  const expectedEndMonth = startMonth + 2;
  if (end.getMonth() !== expectedEndMonth) return false;

  // Last day of expectedEndMonth
  const lastDay = new Date(start.getFullYear(), expectedEndMonth + 1, 0).getDate();
  return end.getDate() === lastDay;
}

/**
 * Detects whether a date range is aligned to calendar months.
 * True when: start is 1st of month AND end is either last day of its month or today.
 */
function isMonthAligned(start: Date, end: Date): boolean {
  if (start.getDate() !== 1) return false;

  // Full month: end is last day of its month
  const lastDayOfEndMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
  if (end.getDate() === lastDayOfEndMonth) return true;

  // Partial "This Month": end is today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endNorm = new Date(end);
  endNorm.setHours(0, 0, 0, 0);
  if (endNorm.getTime() === today.getTime()) return true;

  return false;
}

/**
 * Shifts both endpoints of a date range by `delta` calendar months.
 * End-of-month sentinel: if an endpoint is the last day of its source month,
 * it maps to the last day of the target month (BI-tool "full month" semantics).
 * Otherwise, preserves day-of-month, clamping if the target month is shorter.
 * JS Date handles month overflow/underflow automatically (month -1 = prior Dec, month 12 = Jan of next year).
 */
export const shiftRangeByMonths = (range: DateRange, delta: number): DateRange => {
  if (!range.start || !range.end) return range; // null-guard FIRST LINE
  const shiftEndpoint = (date: Date): Date => {
    const lastDayOfSourceMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const isEndOfMonth = date.getDate() === lastDayOfSourceMonth;
    const targetYear = date.getFullYear();
    const targetMonth = date.getMonth() + delta;
    const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    const targetDay = isEndOfMonth
      ? lastDayOfTargetMonth
      : Math.min(date.getDate(), lastDayOfTargetMonth);
    return new Date(targetYear, targetMonth, targetDay);
  };
  return {
    start: shiftEndpoint(range.start),
    end: shiftEndpoint(range.end),
    isAllTime: range.isAllTime,
  };
};

/**
 * Navigates a date range forward or backward.
 * - Month-aligned ranges snap to full calendar months.
 * - All other ranges jump by their own span (dynamic).
 */
export const navigateDateRange = (
  dateRange: { start: Date | null; end: Date | null; isAllTime?: boolean },
  direction: "prev" | "next"
): { start: Date; end: Date } | null => {
  // D6: All Time mode — prev/next are no-ops (would walk user into a 730d window 730d prior)
  if (dateRange.isAllTime) return null;
  if (!dateRange.start || !dateRange.end) return null;

  const currentStart = new Date(dateRange.start);
  const currentEnd = new Date(dateRange.end);

  currentStart.setHours(0, 0, 0, 0);
  currentEnd.setHours(0, 0, 0, 0);

  // Calendar-year aligned (full year OR year-to-date): navigate by full calendar year
  if (isCalendarYear(currentStart, currentEnd)) {
    const year = currentStart.getFullYear();
    const targetYear = direction === "prev" ? year - 1 : year + 1;
    return {
      start: new Date(targetYear, 0, 1),
      end: new Date(targetYear, 11, 31),
    };
  }

  // Calendar-quarter aligned: navigate by full quarter, wrapping year boundary
  if (isCalendarQuarter(currentStart, currentEnd)) {
    const startMonth = currentStart.getMonth(); // 0, 3, 6, or 9
    const year = currentStart.getFullYear();
    let targetYear = year;
    let targetStartMonth: number;

    if (direction === "prev") {
      if (startMonth === 0) {
        targetYear = year - 1;
        targetStartMonth = 9; // Q4 of prior year
      } else {
        targetStartMonth = startMonth - 3;
      }
    } else {
      if (startMonth === 9) {
        targetYear = year + 1;
        targetStartMonth = 0; // Q1 of next year
      } else {
        targetStartMonth = startMonth + 3;
      }
    }

    return {
      start: new Date(targetYear, targetStartMonth, 1),
      end: new Date(targetYear, targetStartMonth + 3, 0), // last day of (targetStartMonth + 2)
    };
  }

  // Month-aligned: navigate by calendar month
  if (isMonthAligned(currentStart, currentEnd)) {
    if (direction === "prev") {
      const newStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 1);
      const newEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0); // last day of prev month
      return { start: newStart, end: newEnd };
    } else {
      const newStart = new Date(currentEnd.getFullYear(), currentEnd.getMonth() + 1, 1);
      const newEnd = new Date(newStart.getFullYear(), newStart.getMonth() + 1, 0); // last day of new month
      return { start: newStart, end: newEnd };
    }
  }

  // Day-arithmetic: jump by current span
  const dayMs = 1000 * 60 * 60 * 24;
  const diffMs = currentEnd.getTime() - currentStart.getTime();
  const daysDiff = Math.round(diffMs / dayMs) + 1;

  let newStart: Date;
  let newEnd: Date;

  if (direction === "prev") {
    newEnd = new Date(currentStart.getTime() - dayMs);
    newStart = new Date(newEnd.getTime() - (daysDiff - 1) * dayMs);
  } else {
    newStart = new Date(currentEnd.getTime() + dayMs);
    newEnd = new Date(newStart.getTime() + (daysDiff - 1) * dayMs);
  }

  return { start: newStart, end: newEnd };
};
